/**
 * 69.46, the vehicle specialist (DECISIONS 269 (3): the specialists sheet's lean B + C, design_sheets/specialists.html): with
 * GameConfig.crewmanRole on, a squad's crewman works a vehicle's seat faster — the seat reads who sits in it:
 * - **gunning:** a turret he lays turns and elevates at CREWMAN.lay × its rates (core/turret.ts); the main gun he sits at loads
 *   in CREWMAN.reload seconds, not TANK_GUN.reload's (a reload under way when he leaves runs on as it was counted);
 * - **crewing:** a mortar carrier whose tube he holds is set in CREWMAN.setup of MORTAR.setupSeconds (core/mortar.ts). (The
 *   sheet's "in and out of a hatch in half the time" has nothing to halve: a seat is taken in a tick on the action clock.)
 * - **the bots:** a bot crewman aims a seat's gun as a skill CREWMAN.skill higher (to 1), and a squad crewing a vehicle sends
 *   its crewmen to the gun seats first (`crewSeats`), then the rest as before.
 *
 * Who is a crewman: a `crewman` squad slot (65.34's `+crewman`), else — the switch's issue (issueCrewmen, at the setup, after
 * the supports) — each squad of three or more without one names its first plain rifleman. Drawn in look A (the padded crew
 * helmet, the tool roll: rendering/roleLooks.ts). Pure.
 */
import type { GameConfig, GameState, PlayerState, SeatState } from '../types';
import { CREWMAN, TANK_GUN } from '../constants';
import { seatArmed } from './body';
import { defaultOn } from './defaultOn';

/** Is the crewman's seat work in play. */
export function crewmanOn(config: Pick<GameConfig, 'crewmanRole' | 'clock'>): boolean {
  return defaultOn(config, 'crewmanRole');
}

/** Is `p` a crewman whose seat work counts (the switch on, the role his). */
export function isCrewman(state: { config: Pick<GameConfig, 'crewmanRole'> }, p: Pick<PlayerState, 'role'> | null | undefined): boolean {
  return crewmanOn(state.config) && p?.role === 'crewman';
}

/** The share of a turret's rates `unit` lays it at: CREWMAN.lay for a crewman, else 1. */
export function layRate(state: { config: Pick<GameConfig, 'crewmanRole'> }, unit: Pick<PlayerState, 'role'>): number {
  return isCrewman(state, unit) ? CREWMAN.lay : 1;
}

/** The seconds the main gun takes to load a round with `unit` at it. */
export function mainGunReload(state: { config: Pick<GameConfig, 'crewmanRole'> }, unit: Pick<PlayerState, 'role'> | null | undefined): number {
  return isCrewman(state, unit) ? CREWMAN.reload : TANK_GUN.reload;
}

/** The skill a bot aims a seat's gun with: its own, CREWMAN.skill higher for a crewman (to 1). */
export function seatSkill(state: { config: Pick<GameConfig, 'crewmanRole'> }, unit: Pick<PlayerState, 'role'>, skill: number): number {
  return isCrewman(state, unit) ? Math.min(1, skill + CREWMAN.skill) : skill;
}

/** A seat with a gun to work: a bullet gun, a launcher, a shell gun, the tank's main gun or the mortar's tube. */
export function gunSeat(seat: SeatState | undefined): boolean {
  return !!seat && (seatArmed(seat) || !!seat.main || !!seat.mortar);
}

/**
 * The seat each of `ids` (a crew boarding, in its boarding order) takes of `order` (the seats in the boarding's preference):
 * the crewmen first, each to the next gun seat of `order` in turn, then everyone else, in order, to the seats left. Unit id →
 * seat index; a unit with no seat left is absent.
 */
export function crewSeats(order: readonly number[], seats: readonly (SeatState | undefined)[], ids: readonly number[], crew: (id: number) => boolean): Map<number, number> {
  const out = new Map<number, number>(), taken = new Set<number>();
  const guns = order.filter(k => gunSeat(seats[k]));
  for (const id of ids) {
    if (!crew(id)) continue;
    const k = guns.find(g => !taken.has(g));
    if (k === undefined) break;
    out.set(id, k); taken.add(k);
  }
  const rest = order.filter(k => !taken.has(k));
  for (const id of ids) if (!out.has(id) && rest.length) out.set(id, rest.shift()!);
  return out;
}

/** `ids` with the crewmen first (stable: each group in its own order) — a crew's places, so a crewman is not left out of a capped crew. */
export function crewFirst(ids: readonly number[], crew: (id: number) => boolean): number[] {
  return [...ids.filter(crew), ...ids.filter(id => !crew(id))];
}

/** With the switch on, each squad of three or more without a crewman names its first plain rifleman (not its leader, no role). */
export function issueCrewmen(state: GameState): GameState {
  if (!crewmanOn(state.config) || !state.squads?.length) return state;
  const named = new Set<number>();
  for (const sq of state.squads) {
    if (sq.unitIds.length < 3) continue;
    const ms = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p);
    if (ms.some(p => p.role === 'crewman')) continue;
    const pick = ms.find(p => (p.primary ?? 'rifle') === 'rifle' && !p.secondary && !p.role && p.id !== sq.leader);
    if (pick) named.add(pick.id);
  }
  if (!named.size) return state;
  return { ...state, players: state.players.map(p => (named.has(p.id) ? { ...p, role: 'crewman' as const } : p)) };
}
